import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  IconCircleChevronDown,
  IconCircleChevronUp,
  ChevronDown,
} from "../../../common/Icons/Icons";
import styles from "./GroupPicker.module.css";

/**
 * A tree-based group picker component for selecting groups from a hierarchy.
 *
 * @param {Object} props
 * @param {Array} props.organizations - Array of { org, groups } where groups have { group, depth }
 * @param {Array} props.standalone - Array of standalone groups { group, depth }
 * @param {number|null} props.selectedGroupId - Currently selected group ID
 * @param {function} props.onSelect - Callback when a group is selected (receives group id)
 */
const GroupPicker = ({ organizations, standalone, selectedGroupId, onSelect }) => {
  const { t } = useTranslation("common");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [expandedNodes, setExpandedNodes] = useState(() => {
    // Start with all organizations expanded
    const initial = new Set();
    organizations?.forEach(({ org }) => {
      initial.add(`org-${org.id}`);
    });
    return initial;
  });

  // Update dropdown position when opened
  useEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Find selected group name for display
  const selectedGroupName = useMemo(() => {
    if (!selectedGroupId) return null;

    // Check organizations
    for (const { org, groups } of (organizations || [])) {
      if (org.id === selectedGroupId) return org.name;
      for (const { group } of groups) {
        if (group.id === selectedGroupId) return group.name;
      }
    }

    // Check standalone
    for (const { group } of (standalone || [])) {
      if (group.id === selectedGroupId) return group.name;
    }

    return null;
  }, [selectedGroupId, organizations, standalone]);

  // Build tree structure from flat groups with depth
  const buildTreeFromGroups = useCallback((flatGroups) => {
    if (!flatGroups || flatGroups.length === 0) return [];

    // Create nodes with children arrays
    const nodeMap = new Map();
    flatGroups.forEach(({ group, depth }) => {
      nodeMap.set(group.id, { ...group, depth, children: [] });
    });

    // Build tree by linking children to parents based on depth order
    const roots = [];
    const stack = []; // Stack of { node, depth }

    flatGroups.forEach(({ group, depth }) => {
      const node = nodeMap.get(group.id);

      // Pop stack until we find the parent (depth - 1)
      while (stack.length > 0 && stack[stack.length - 1].depth >= depth) {
        stack.pop();
      }

      if (stack.length === 0) {
        // Root node
        roots.push(node);
      } else {
        // Child of the node at top of stack
        stack[stack.length - 1].node.children.push(node);
      }

      stack.push({ node, depth });
    });

    return roots;
  }, []);

  // Toggle expand/collapse
  const toggleNode = useCallback((nodeKey) => {
    setExpandedNodes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(nodeKey)) {
        newSet.delete(nodeKey);
      } else {
        newSet.add(nodeKey);
      }
      return newSet;
    });
  }, []);

  const handleSelect = useCallback((groupId) => {
    onSelect(groupId);
    setIsOpen(false);
  }, [onSelect]);

  const hasGroups = organizations?.length > 0 || standalone?.length > 0;

  return (
    <>
      <div className={styles.container} ref={containerRef}>
        {/* Trigger button */}
        <button
          type="button"
          className={styles.trigger}
          onClick={() => setIsOpen(!isOpen)}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          <span className={selectedGroupName ? styles.selectedText : styles.placeholderText}>
            {selectedGroupName || t("sharePlan.selectGroupPlaceholder")}
          </span>
          <ChevronDown className={styles.triggerIcon} />
        </button>
      </div>

      {/* Dropdown rendered via portal */}
      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className={styles.dropdown}
          style={{
            position: 'fixed',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
          }}
        >
          {!hasGroups ? (
            <div className={styles.emptyState}>
              {t("sharePlan.noGroupsAvailable", "Ingen grupper tilgjengelig")}
            </div>
          ) : (
            <>
              {organizations?.map(({ org, groups }) => (
                <OrganizationSection
                  key={org.id}
                  organization={org}
                  groups={groups}
                  buildTree={buildTreeFromGroups}
                  expandedNodes={expandedNodes}
                  onToggleNode={toggleNode}
                  selectedGroupId={selectedGroupId}
                  onSelect={handleSelect}
                />
              ))}

              {standalone?.length > 0 && (
                <div className={styles.standaloneSection}>
                  <div className={styles.sectionHeader}>
                    {t("sharePlan.otherGroups")}
                  </div>
                  {standalone.map(({ group }) => (
                    <GroupRow
                      key={group.id}
                      group={group}
                      level={0}
                      expandedNodes={expandedNodes}
                      onToggleNode={toggleNode}
                      selectedGroupId={selectedGroupId}
                      onSelect={handleSelect}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>,
        document.body
      )}
    </>
  );
};

const OrganizationSection = ({
  organization,
  groups,
  buildTree,
  expandedNodes,
  onToggleNode,
  selectedGroupId,
  onSelect,
}) => {
  const nodeKey = `org-${organization.id}`;
  const isExpanded = expandedNodes.has(nodeKey);

  // Build tree from flat groups
  const tree = useMemo(() => buildTree(groups), [groups, buildTree]);
  const hasChildren = tree.length > 0;

  return (
    <div className={styles.organizationSection}>
      {/* Organization header row */}
      <div
        className={`${styles.row} ${styles.orgRow} ${selectedGroupId === organization.id ? styles.selected : ""}`}
        onClick={() => onSelect(organization.id)}
      >
        <div className={styles.rowContent}>
          {hasChildren && (
            <button
              type="button"
              className={styles.expandButton}
              onClick={(e) => {
                e.stopPropagation();
                onToggleNode(nodeKey);
              }}
              aria-label={isExpanded ? "Collapse" : "Expand"}
            >
              {isExpanded ? <IconCircleChevronUp /> : <IconCircleChevronDown />}
            </button>
          )}
          <span className={styles.groupName}>{organization.name}</span>
        </div>
        <div className={styles.selectIndicator}>
          {selectedGroupId === organization.id && <span className={styles.checkmark}>✓</span>}
        </div>
      </div>

      {/* Children */}
      {isExpanded && hasChildren && (
        <div className={styles.children}>
          {tree.map((group) => (
            <GroupRow
              key={group.id}
              group={group}
              level={1}
              expandedNodes={expandedNodes}
              onToggleNode={onToggleNode}
              selectedGroupId={selectedGroupId}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const GroupRow = ({
  group,
  level,
  expandedNodes,
  onToggleNode,
  selectedGroupId,
  onSelect,
}) => {
  const nodeKey = `group-${group.id}`;
  const isExpanded = expandedNodes.has(nodeKey);
  const hasChildren = group.children && group.children.length > 0;
  const isSelected = selectedGroupId === group.id;

  const paddingLeft = level * 1.5; // 1.5rem per level

  return (
    <>
      <div
        className={`${styles.row} ${isSelected ? styles.selected : ""}`}
        style={{ paddingLeft: `${paddingLeft}rem` }}
        onClick={() => onSelect(group.id)}
      >
        <div className={styles.rowContent}>
          {hasChildren ? (
            <button
              type="button"
              className={styles.expandButton}
              onClick={(e) => {
                e.stopPropagation();
                onToggleNode(nodeKey);
              }}
              aria-label={isExpanded ? "Collapse" : "Expand"}
            >
              {isExpanded ? <IconCircleChevronUp /> : <IconCircleChevronDown />}
            </button>
          ) : (
            <span className={styles.expandSpacer} />
          )}
          <span className={styles.groupName}>{group.name}</span>
        </div>
        <div className={styles.selectIndicator}>
          {isSelected && <span className={styles.checkmark}>✓</span>}
        </div>
      </div>

      {/* Children */}
      {isExpanded && hasChildren && (
        <>
          {group.children.map((child) => (
            <GroupRow
              key={child.id}
              group={child}
              level={level + 1}
              expandedNodes={expandedNodes}
              onToggleNode={onToggleNode}
              selectedGroupId={selectedGroupId}
              onSelect={onSelect}
            />
          ))}
        </>
      )}
    </>
  );
};

export default GroupPicker;
